import React, { useEffect, useState } from "react";
import { Card, Row, Col, Select, DatePicker, Button } from "antd";
import MobileDatePicker from "./common/mobileDatePicker";
import dayjs, { Dayjs } from "dayjs";
import minMax from "dayjs/plugin/minMax";
import { getWidth } from "../util";
import Bar from "./Bar";
import { CalendarOutlined, SwapRightOutlined } from "@ant-design/icons";
import {
  GetAppDataRequest,
  GetAppInfosRequest,
  GetUniqAddressRequest,
} from "../proto/brevis_explorer/statistics_pb";
import {
  getAddressNum,
  getAppInfos,
  getAppRequestNum,
  getProofsNum,
} from "../store/grpc_client";
import { useRef } from "react";

import "./ChartSection.css";

const { RangePicker } = DatePicker;
dayjs.extend(minMax);

interface PresetItem {
  label: string;
  value: [Dayjs, Dayjs];
  key: string;
}

const ChartSection: React.FC = () => {
  const myRef = useRef(null);
  const now = dayjs();
  const thirtyDaysAgo = now.subtract(30, "day");
  let isMobile = getWidth() <= 414 ? true : false;

  const [proofNum, setProofNum] = useState<number>(0);
  const [proofChartDate, setProofChartDate] = useState<string[]>([]);
  const [proofChartValue, setProofChartValue] = useState<number[]>([]);
  const [proofDateRange, setProofDateRange] = useState<[Dayjs, Dayjs] | null>([
    thirtyDaysAgo,
    now,
  ]);

  const [appsNum, setAppsNum] = useState<number>(0);
  const [appsChartDate, setAppsChartDate] = useState<string[]>([]);
  const [appsChartValue, setAppsChartValue] = useState<number[]>([]);
  const [appsDateRange, setAppsDateRange] = useState<[Dayjs, Dayjs] | null>([
    thirtyDaysAgo,
    now,
  ]);

  const [addressNum, setAddressNum] = useState<number>(0);
  const [addressChartDate, setAddressChartDate] = useState<string[]>([]);
  const [addressChartValue, setAddressChartValue] = useState<number[]>([]);
  const [addressDateRange, setAddressDateRange] = useState<
    [Dayjs, Dayjs] | null
  >([thirtyDaysAgo, now]);

  const [proofSelectedApp, setProofSelectedApp] = useState<string[]>([]);
  const [appsSelectedApp, setAppSelectedApp] = useState<string[]>([]);
  const [addressSelectedApp, setAddressSelectedApp] = useState<string[]>([]);

  const [activePreset, setActivePreset] = useState<string | null>(null);

  // for mobile date picker
  const [proofStartVisible, setProofStartVisible] = useState<boolean>(false);
  const [proofEndVisible, setProofEndVisible] = useState<boolean>(false);
  const [proofStartDate, setProofStartDate] = useState<Dayjs>(thirtyDaysAgo);
  const [proofEndDate, setProofEndDate] = useState<Dayjs>(now);

  const [appsStartVisible, setAppsStartVisible] = useState<boolean>(false);
  const [appsEndVisible, setAppsEndVisible] = useState<boolean>(false);
  const [appsStartDate, setAppsStartDate] = useState<Dayjs>(thirtyDaysAgo);
  const [appsEndDate, setAppsEndDate] = useState<Dayjs>(now);

  const [addressStartVisible, setAddressStartVisible] =
    useState<boolean>(false);
  const [addressEndVisible, setAddressEndVisible] = useState<boolean>(false);
  const [addressStartDate, setAddressStartDate] =
    useState<Dayjs>(thirtyDaysAgo);
  const [addressEndDate, setAddressEndDate] = useState<Dayjs>(now);

  // const [proofAppVisible, setProofAppVisible] = useState<boolean>(false);
  // const [proofSelectedAppValue, setProofSelectedAppValue] = useState<(string[])>([]);
  // const [proofSelectedAppLable, setProofSelectedAppLabel] = useState<string>('All Apps');

  // const [appsAppVisible, setAppsAppVisible] = useState<boolean>(false);
  // const [appSelectedAppValue, setAppSelectedAppValue] = useState<(string[])>([]);
  // const [appSelectedAppLable, setAppSelectedAppLable] = useState<string>('All Apps');

  // const [addressAppVisible, setAddressAppVisible] = useState<boolean>(false);
  // const [addressSelectedAppValue, setAddressSelectedAppValue] = useState<(string[])>();
  // const [addressSelectedAppLable, setAddressSelectedAppLable] = useState<string>('All Apps');

  const [appOptions, setAppOptions] = useState<
    { label: React.ReactNode; value: string }[]
  >([]);
  // const [appColumns, setAppColumns] = useState<{ label: React.ReactNode; value: string }[][]>([]);

  useEffect(() => {
    const req = new GetAppInfosRequest();
    getAppInfos(req).then((res) => {
      if (res) {
        const { appsList } = res.toObject();
        const options: { label: React.ReactNode; value: string }[] =
          appsList.map((app) => ({
            label: (
              <>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <img
                    src={app.iconUrl}
                    alt=""
                    style={{ width: 24, height: 24, marginRight: 12 }}
                  />
                  <span>{app.displayName}</span>
                </div>
                <div className="searchBox"></div>
              </>
            ),
            value: app.name,
          }));
        options.unshift({
          label: (
            <div style={{ display: "flex", alignItems: "center" }}>
              <img
                src={"/all_apps.svg"}
                alt=""
                style={{ width: 24, height: 24, marginRight: 12 }}
              />
              <span>{"All Apps"}</span>
            </div>
          ),
          value: "",
        });
        setAppOptions(options);
        // let appColumns = [{ label: 'All Apps', value: '' }, ...options];
        // setAppColumns([options]);
      }
    });
  }, []);

  useEffect(() => {
    if (!proofDateRange || !proofStartDate || !proofEndDate) {
      return;
    }
    let start = 0;
    let end = 0;
    let appNames;
    if (isMobile) {
      start = proofStartDate.valueOf();
      end = proofEndDate.valueOf();
    } else {
      start = proofDateRange[0].valueOf();
      end = proofDateRange[1].valueOf();
    }
    appNames = proofSelectedApp;
    let req = new GetAppDataRequest();
    appNames && req.setAppNamesList(appNames);
    req.setStartTime(start);
    req.setEndTime(end);
    getProofsNum(req).then((res) => {
      if (res) {
        const { dailyStatisticList } = res.toObject();
        let proofNum = 0;
        let proofChartDate: string[] = [];
        let proofChartValue: number[] = [];
        const dateToNumMap = new Map<string, number>();
        let dateList: dayjs.Dayjs[] = [];
        dailyStatisticList.forEach((item) => {
          proofNum += item.num;
          const date = dayjs(item.date).startOf("day");
          const dateStr = date.format("YYYY-MM-DD");
          dateToNumMap.set(dateStr, item.num);
          dateList.push(date);
        });
        // const minDate = dayjs.min(dateList);
        // const maxDate = dayjs.max(dateList);
        const minDate = dayjs(start);
        const maxDate = dayjs(end);
        if (minDate && maxDate) {
          let current = minDate;
          while (current.isBefore(maxDate) || current.isSame(maxDate, "day")) {
            const currentStr = current.format("YYYY-MM-DD");
            const displayStr = current.format("MMM DD, YYYY");
            proofChartDate.push(displayStr);
            proofChartValue.push(dateToNumMap.get(currentStr) || 0);
            current = current.add(1, "day");
          }
        }
        setProofNum(proofNum);
        setProofChartDate(proofChartDate);
        setProofChartValue(proofChartValue);
      }
    });
  }, [
    proofSelectedApp,
    proofDateRange,
    isMobile,
    proofStartDate,
    proofEndDate,
  ]);

  useEffect(() => {
    if (!appsDateRange || !appsStartDate || !appsEndDate) {
      return;
    }
    let start = 0;
    let end = 0;
    let appNames;
    if (isMobile) {
      start = appsStartDate.valueOf();
      end = appsEndDate.valueOf();
    } else {
      start = appsDateRange[0].valueOf();
      end = appsDateRange[1].valueOf();
    }
    appNames = appsSelectedApp;
    let req = new GetAppDataRequest();
    appNames && req.setAppNamesList(appNames);
    req.setStartTime(start);
    req.setEndTime(end);
    getAppRequestNum(req).then((res) => {
      if (res) {
        const { dailyStatisticList } = res.toObject();
        let appsNum = 0;
        let appsChartDate: string[] = [];
        let appsChartValue: number[] = [];
        const dateToNumMap = new Map<string, number>();
        let dateList: dayjs.Dayjs[] = [];
        dailyStatisticList.forEach((item) => {
          appsNum += item.num;
          const date = dayjs(item.date).startOf("day");
          const dateStr = date.format("YYYY-MM-DD");
          dateToNumMap.set(dateStr, item.num);
          dateList.push(date);
        });
        // const minDate = dayjs.min(dateList);
        // const maxDate = dayjs.max(dateList);
        const minDate = dayjs(start);
        const maxDate = dayjs(end);
        if (minDate && maxDate) {
          let current = minDate;
          while (current.isBefore(maxDate) || current.isSame(maxDate, "day")) {
            const currentStr = current.format("YYYY-MM-DD");
            const displayStr = current.format("MMM DD, YYYY");
            appsChartDate.push(displayStr);
            appsChartValue.push(dateToNumMap.get(currentStr) || 0);
            current = current.add(1, "day");
          }
        }
        setAppsNum(appsNum);
        setAppsChartDate(appsChartDate);
        setAppsChartValue(appsChartValue);
      }
    });
  }, [appsSelectedApp, appsDateRange, isMobile, appsStartDate, appsEndDate]);

  useEffect(() => {
    if (!addressDateRange || !addressStartDate || !addressEndDate) {
      return;
    }
    let start = 0;
    let end = 0;
    let appNames;
    if (isMobile) {
      start = addressStartDate.valueOf();
      end = addressEndDate.valueOf();
    } else {
      start = addressDateRange[0].valueOf();
      end = addressDateRange[1].valueOf();
    }
    appNames = addressSelectedApp;
    let req = new GetUniqAddressRequest();
    appNames && req.setAppNamesList(appNames);
    req.setStartTime(start);
    req.setEndTime(end);
    getAddressNum(req).then((res) => {
      if (res) {
        const { dailyStatisticList, totalNum } = res.toObject();
        let addressChartDate: string[] = [];
        let addressChartValue: number[] = [];
        const dateToNumMap = new Map<string, number>();
        let dateList: dayjs.Dayjs[] = [];
        dailyStatisticList.forEach((item) => {
          const date = dayjs(item.date).startOf("day");
          const dateStr = date.format("YYYY-MM-DD");
          dateToNumMap.set(dateStr, item.num);
          dateList.push(date);
        });
        // const minDate = dayjs.min(dateList);
        // const maxDate = dayjs.max(dateList);
        const minDate = dayjs(start);
        const maxDate = dayjs(end);
        if (minDate && maxDate) {
          let current = minDate;
          while (current.isBefore(maxDate) || current.isSame(maxDate, "day")) {
            const currentStr = current.format("YYYY-MM-DD");
            const displayStr = current.format("MMM DD, YYYY");
            addressChartDate.push(displayStr);
            addressChartValue.push(dateToNumMap.get(currentStr) || 0);
            current = current.add(1, "day");
          }
        }
        setAddressNum(totalNum);
        setAddressChartDate(addressChartDate);
        setAddressChartValue(addressChartValue);
      }
    });
  }, [
    addressSelectedApp,
    addressDateRange,
    isMobile,
    addressStartDate,
    addressEndDate,
  ]);

  const handleProofStartConfirm = (value: Date) => {
    setProofStartDate(dayjs(value));
    setProofStartVisible(false);
    if (proofDateRange) {
      setProofDateRange([dayjs(value), proofDateRange[1]]);
    } else {
      setProofDateRange([dayjs(value), dayjs()]);
    }

    setTimeout(() => setProofEndVisible(true), 300);
  };

  const handleAppsStartConfirm = (value: Date) => {
    setAppsStartDate(dayjs(value));
    setAppsStartVisible(false);
    if (appsDateRange) {
      setAppsDateRange([dayjs(value), appsDateRange[1]]);
    } else {
      setAppsDateRange([dayjs(value), dayjs()]);
    }

    setTimeout(() => setAppsEndVisible(true), 300);
  };

  const handleAddressStartConfirm = (value: Date) => {
    setAddressStartDate(dayjs(value));
    setAddressStartVisible(false);
    if (addressDateRange) {
      setAddressDateRange([dayjs(value), addressDateRange[1]]);
    } else {
      setAddressDateRange([dayjs(value), dayjs()]);
    }

    setTimeout(() => setAddressEndVisible(true), 300);
  };

  // const handleMProofAppConfirm = (value: any, label: any) => {
  //   setProofSelectedAppLabel(label);
  //   setProofSelectedAppValue(value[0]);
  //   setProofAppVisible(false);
  // };

  // const handleMAppsAppConfirm = (value: any, label: any) => {
  //   setAppSelectedAppLable(label);
  //   setAppSelectedAppValue(value[0]);
  //   setAppsAppVisible(false);
  // };

  // const handleMAddressAppConfirm = (value: any, label: any) => {
  //   setAddressSelectedAppLable(label);
  //   setAddressSelectedAppValue(value[0]);
  //   setAddressAppVisible(false);
  // };

  const handleProofEndConfirm = (value: Date) => {
    setProofEndDate(dayjs(value));
    setProofEndVisible(false);

    if (proofDateRange) {
      setProofDateRange([proofDateRange[0], dayjs(value)]);
    } else {
      setProofDateRange([dayjs(proofStartDate), dayjs(value)]);
    }

    setActivePreset(null);
  };

  const handleAppsEndConfirm = (value: Date) => {
    setAppsEndDate(dayjs(value));
    setAppsEndVisible(false);

    if (appsDateRange) {
      setAppsDateRange([appsDateRange[0], dayjs(value)]);
    } else {
      setAppsDateRange([dayjs(appsStartDate), dayjs(value)]);
    }

    setActivePreset(null);
  };

  const handleAddressEndConfirm = (value: Date) => {
    setAddressEndDate(dayjs(value));
    setAddressEndVisible(false);

    if (addressDateRange) {
      setAddressDateRange([addressDateRange[0], dayjs(value)]);
    } else {
      setAddressDateRange([dayjs(addressStartDate), dayjs(value)]);
    }

    setActivePreset(null);
  };

  const handleProofAppChange = (values: string[]) => {
    let proofSelectedApps: string[] = [];
    values.forEach((item: string) => {
      if (item) {
        proofSelectedApps.push(item);
      } else {
        proofSelectedApps = [];
      }
    });
    setProofSelectedApp(proofSelectedApps);
  };
  const handleAppsAppChange = (values: string[]) => {
    let appsSelectedApps: string[] = [];
    values.forEach((item: string) => {
      if (item) {
        appsSelectedApps.push(item);
      } else {
        appsSelectedApps = [];
      }
    });
    setAppSelectedApp(appsSelectedApps);
  };
  const handleAddressAppChange = (values: string[]) => {
    let addressSelectedApps: string[] = [];
    values.forEach((item: string) => {
      if (item) {
        addressSelectedApps.push(item);
      } else {
        addressSelectedApps = [];
      }
    });
    setAddressSelectedApp(addressSelectedApps);
  };

  const handleProofDateChange = (dates: any) => {
    console.log(dates);
    if (dates) {
      setProofDateRange(dates);
    } else {
      setProofDateRange([thirtyDaysAgo, now]);
    }
    setActivePreset(null);
  };

  const handleAppsDateChange = (dates: any) => {
    setAppsDateRange(dates);
    setActivePreset(null);
  };

  const handleAddressDateChange = (dates: any) => {
    setAddressDateRange(dates);
    setActivePreset(null);
  };

  const handlePresetClick = (preset: string, value: [Dayjs, Dayjs]) => {
    setActivePreset(preset);
  };

  const presets: PresetItem[] = [
    {
      label: "Last 7 days",
      value: [dayjs().subtract(7, "day"), dayjs()],
      key: "last7days",
    },
    {
      label: "Last 30 days",
      value: [dayjs().subtract(30, "day"), dayjs()],
      key: "last30days",
    },
    {
      label: "Today",
      value: [dayjs().startOf("day"), dayjs()],
      key: "today",
    },
    {
      label: "This Month",
      value: [dayjs().startOf("month"), dayjs()],
      key: "thisMonth",
    },
  ];

  const renderProofPresetButtons = () => {
    return (
      <div className="date-presets">
        {presets.map((preset) => (
          <Button
            key={preset.key}
            className={`preset-btn ${
              activePreset === preset.key ? "active" : ""
            }`}
            onClick={() => {
              setProofDateRange(preset.value);
              handlePresetClick(preset.key, preset.value);
            }}
          >
            {preset.label}
          </Button>
        ))}
      </div>
    );
  };
  const renderAppsPresetButtons = () => {
    return (
      <div className="date-presets">
        {presets.map((preset) => (
          <Button
            key={preset.key}
            className={`preset-btn ${
              activePreset === preset.key ? "active" : ""
            }`}
            onClick={() => {
              setAppsDateRange(preset.value);
              handlePresetClick(preset.key, preset.value);
            }}
          >
            {preset.label}
          </Button>
        ))}
      </div>
    );
  };
  const renderAddressPresetButtons = () => {
    return (
      <div className="date-presets">
        {presets.map((preset) => (
          <Button
            key={preset.key}
            className={`preset-btn ${
              activePreset === preset.key ? "active" : ""
            }`}
            onClick={() => {
              setAddressDateRange(preset.value);
              handlePresetClick(preset.key, preset.value);
            }}
          >
            {preset.label}
          </Button>
        ))}
      </div>
    );
  };

  return (
    <div className="chart-section">
      <Row gutter={[0, 20]}>
        <Col span={24}>
          <Card className="chart-card">
            <div className="chart-title">
              <div className="chart-title-des">
                <div>
                  Number of Proofs Generated:{" "}
                  <span className="chart-count">
                    {proofNum.toLocaleString()}
                  </span>
                </div>
              </div>
              <div className="app-space">
                <Select
                  ref={myRef}
                  showSearch={false}
                  mode="multiple"
                  placeholder="All Apps"
                  value={proofSelectedApp}
                  onChange={handleProofAppChange}
                  dropdownStyle={{
                    backgroundColor: "#22252E",
                    color: "#FFFCE4",
                  }}
                  popupClassName="app-select"
                  options={appOptions}
                  tagRender={(props) => {
                    return (
                      <div className="selectShow">
                        <div className="searchItemNum">
                          {proofSelectedApp?.length}
                        </div>
                        <div className="selectShowText">Apps</div>
                      </div>
                    );
                  }}
                />
                <div className="date-picker-container">
                  <RangePicker
                    value={proofDateRange}
                    onChange={handleProofDateChange}
                    format="MMM DD, YYYY"
                    popupClassName="brevis-range-picker"
                    renderExtraFooter={() => renderProofPresetButtons()}
                  />
                </div>
                <div className="mobile-date-picker-container">
                  {/* <div className="mobile-picker-btn" style={{ marginBottom: "20px" }}>
                    <div className="mobile-picker-btn-left" onClick={() => {setProofAppVisible(true)}}>
                      <div className="start">{proofSelectedAppLable}</div>
                    </div>
                    <div className="mobile-picker-btn-right">
                      <DownOutlined />
                    </div>
                  </div> */}

                  <div className="mobile-picker-btn">
                    <div className="mobile-picker-btn-left">
                      <div
                        className="start"
                        onClick={() => {
                          setProofStartVisible(true);
                        }}
                      >
                        {proofStartDate
                          ? proofStartDate.format("MMM DD, YYYY")
                          : "Start date"}
                      </div>
                      <SwapRightOutlined />
                      <div
                        className="end"
                        onClick={() => {
                          setProofEndVisible(true);
                        }}
                      >
                        {proofEndDate
                          ? proofEndDate.format("MMM DD, YYYY")
                          : "End date"}
                      </div>
                    </div>
                    <div className="mobile-picker-btn-right">
                      <CalendarOutlined />
                    </div>
                  </div>
                  <MobileDatePicker
                    title="Select Start Date"
                    visible={proofStartVisible}
                    onClose={() => {
                      setProofStartVisible(false);
                    }}
                    // max={new Date()}
                    defaultValue={proofStartDate.toDate()}
                    confirmText="OK"
                    cancelText=""
                    onConfirm={handleProofStartConfirm}
                  />
                  <MobileDatePicker
                    title="Select End Date"
                    visible={proofEndVisible}
                    onClose={() => {
                      setProofEndVisible(false);
                    }}
                    // max={new Date()}
                    defaultValue={proofEndDate.toDate()}
                    confirmText="OK"
                    cancelText=""
                    onConfirm={handleProofEndConfirm}
                  />
                  {/* <AppPicker
                    visible={proofAppVisible}
                    options={appColumns}
                    onConfirm={handleMProofAppConfirm}
                    onClose={() => {
                      setProofAppVisible(false)
                    }}
                  /> */}
                </div>
              </div>
            </div>
            <Bar
              id={"1"}
              seriesName="Number"
              series={proofChartValue}
              xAxis={proofChartDate}
            />
          </Card>
        </Col>
        <Col span={24}>
          <Card className="chart-card">
            <div className="chart-title">
              <div className="chart-title-des">
                <div>
                  Number of App Requests:{" "}
                  <span className="chart-count">
                    {appsNum.toLocaleString()}
                  </span>
                </div>
              </div>
              <div className="app-space">
                <Select
                  mode="multiple"
                  showSearch={false}
                  placeholder="All Apps"
                  value={appsSelectedApp}
                  onChange={handleAppsAppChange}
                  dropdownStyle={{
                    backgroundColor: "#22252E",
                    color: "#FFFCE4",
                  }}
                  popupClassName="app-select"
                  options={appOptions}
                  tagRender={(props) => {
                    return (
                      <div className="selectShow">
                        <div className="searchItemNum">
                          {appsSelectedApp?.length}
                        </div>
                        <div className="selectShowText">Apps</div>
                      </div>
                    );
                  }}
                />
                <div className="date-picker-container">
                  <RangePicker
                    value={appsDateRange}
                    onChange={handleAppsDateChange}
                    format="MMM DD, YYYY"
                    // showTime={{ format: 'HH'}}
                    popupClassName="brevis-range-picker"
                    renderExtraFooter={() => renderAppsPresetButtons()}
                  />
                </div>
                <div className="mobile-date-picker-container">
                  {/* <div className="mobile-picker-btn" style={{ marginBottom: "20px" }}>
                    <div className="mobile-picker-btn-left" onClick={() => {setAppsAppVisible(true)}}>
                      <div className="start">{appSelectedAppLable}</div>
                    </div>
                    <div className="mobile-picker-btn-right">
                      <DownOutlined />
                    </div>
                  </div> */}

                  <div className="mobile-picker-btn">
                    <div className="mobile-picker-btn-left">
                      <div
                        className="start"
                        onClick={() => {
                          setAppsStartVisible(true);
                        }}
                      >
                        {appsStartDate
                          ? appsStartDate.format("MMM DD, YYYY")
                          : "Start date"}
                      </div>
                      <SwapRightOutlined />
                      <div
                        className="end"
                        onClick={() => {
                          setAppsEndVisible(true);
                        }}
                      >
                        {appsEndDate
                          ? appsEndDate.format("MMM DD, YYYY")
                          : "End date"}
                      </div>
                    </div>
                    <div className="mobile-picker-btn-right">
                      <CalendarOutlined />
                    </div>
                  </div>
                  <MobileDatePicker
                    title="Select Start Date"
                    visible={appsStartVisible}
                    onClose={() => {
                      setAppsStartVisible(false);
                    }}
                    // max={new Date()}
                    defaultValue={appsStartDate.toDate()}
                    confirmText="OK"
                    cancelText=""
                    onConfirm={handleAppsStartConfirm}
                  />
                  <MobileDatePicker
                    title="Select End Date"
                    visible={appsEndVisible}
                    onClose={() => {
                      setAppsEndVisible(false);
                    }}
                    // max={new Date()}
                    defaultValue={appsEndDate.toDate()}
                    confirmText="OK"
                    cancelText=""
                    onConfirm={handleAppsEndConfirm}
                  />
                  {/* <AppPicker
                    visible={appsAppVisible}
                    options={appColumns}
                    onConfirm={handleMAppsAppConfirm}
                    onClose={() => {
                      setAppsAppVisible(false)
                    }}
                  /> */}
                </div>
              </div>
            </div>
            <Bar
              id={"2"}
              seriesName="Number"
              series={appsChartValue}
              xAxis={appsChartDate}
            />
          </Card>
        </Col>
        <Col span={24}>
          <Card className="chart-card">
            <div className="chart-title">
              <div className="chart-title-des">
                <div>
                  Number of Unique Addresses:{" "}
                  <span className="chart-count">
                    {addressNum.toLocaleString()}
                  </span>
                </div>
              </div>
              <div className="app-space">
                <Select
                  mode="multiple"
                  showSearch={false}
                  value={addressSelectedApp}
                  placeholder="All Apps"
                  onChange={handleAddressAppChange}
                  dropdownStyle={{
                    backgroundColor: "#22252E",
                    color: "#FFFCE4",
                  }}
                  popupClassName="app-select"
                  options={appOptions}
                  tagRender={(props) => {
                    return (
                      <div className="selectShow">
                        <div className="searchItemNum">
                          {addressSelectedApp?.length}
                        </div>
                        <div className="selectShowText">Apps</div>
                      </div>
                    );
                  }}
                />
                <div className="date-picker-container">
                  <RangePicker
                    value={addressDateRange}
                    onChange={handleAddressDateChange}
                    format="MMM DD, YYYY"
                    // showTime={{ format: 'HH'}}
                    popupClassName="brevis-range-picker"
                    renderExtraFooter={() => renderAddressPresetButtons()}
                  />
                </div>
                <div className="mobile-date-picker-container">
                  {/* <div className="mobile-picker-btn" style={{ marginBottom: "20px" }}>
                    <div className="mobile-picker-btn-left" onClick={() => {setAddressAppVisible(true)}}>
                      <div className="start">{addressSelectedAppLable}</div>
                    </div>
                    <div className="mobile-picker-btn-right">
                      <DownOutlined />
                    </div>
                  </div> */}

                  <div className="mobile-picker-btn">
                    <div className="mobile-picker-btn-left">
                      <div
                        className="start"
                        onClick={() => {
                          setAddressStartVisible(true);
                        }}
                      >
                        {addressStartDate
                          ? addressStartDate.format("MMM DD, YYYY")
                          : "Start date"}
                      </div>
                      <SwapRightOutlined />
                      <div
                        className="end"
                        onClick={() => {
                          setAddressEndVisible(true);
                        }}
                      >
                        {addressEndDate
                          ? addressEndDate.format("MMM DD, YYYY")
                          : "End date"}
                      </div>
                    </div>
                    <div className="mobile-picker-btn-right">
                      <CalendarOutlined />
                    </div>
                  </div>
                  <MobileDatePicker
                    title="Select Start Date"
                    visible={addressStartVisible}
                    onClose={() => {
                      setAddressStartVisible(false);
                    }}
                    // max={new Date()}
                    defaultValue={addressStartDate.toDate()}
                    confirmText="OK"
                    cancelText=""
                    onConfirm={handleAddressStartConfirm}
                  />
                  <MobileDatePicker
                    title="Select End Date"
                    visible={addressEndVisible}
                    onClose={() => {
                      setAddressEndVisible(false);
                    }}
                    // max={new Date()}
                    defaultValue={addressEndDate.toDate()}
                    confirmText="OK"
                    cancelText=""
                    onConfirm={handleAddressEndConfirm}
                  />
                  {/* <AppPicker
                    visible={addressAppVisible}
                    options={appColumns}
                    onConfirm={handleMAddressAppConfirm}
                    onClose={() => {
                      setAddressAppVisible(false)
                    }}
                  /> */}
                </div>
              </div>
            </div>
            <Bar
              id={"3"}
              seriesName="Number"
              series={addressChartValue}
              xAxis={addressChartDate}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ChartSection;
